import { EChartsCoreOption } from './echartsCore';
import { TOOLTIP_BASE } from './tooltipAdapter';

const INACTIVE_OPACITY = 0.2;

export interface PieSlice {
    name: string;
    value: number;
    color?: string;
    formatted?: string;
    url?: string;
    queryParameters?: string;
}

export interface PiePoint {
    name: string;
    y: number;
    percentage: number;
    formatted: string;
    url?: string;
    queryParameters?: string;
}

interface PieParamsLike {
    name?: string;
    value?: unknown;
    percent?: number;
    data?: Partial<PieSlice>;
}

export const toPiePoint = (params: PieParamsLike): PiePoint => {
    const slice = params.data ?? {};
    const raw = typeof params.value === 'number' ? params.value : slice.value;
    const value = typeof raw === 'number' ? raw : 0;
    return {
        name: params.name ?? slice.name ?? '',
        y: value,
        percentage: params.percent ?? 0,
        formatted: slice.formatted ?? String(value),
        url: slice.url,
        queryParameters: slice.queryParameters,
    };
};

export const defaultPieTooltip = (point: PiePoint, valueLabel: string): string => `
            <div>
                <b>${point.name}</b>
                <div class="d-flex justify-content-between">
                    <span>${valueLabel}:</span>
                    <b style="padding-left: 20px">${point.formatted}</b>
                </div>
                <div class="d-flex justify-content-between">
                    <span>Time Percentage:</span>
                    <b style="padding-left: 20px">${point.percentage.toFixed(0)}%</b>
                </div>
            </div>`;

export interface PieOptionArgs {
    slices: PieSlice[];
    tooltipFormatter?: (point: PiePoint) => string;
    valueLabel?: string;
}

export const buildPieOption = (args: PieOptionArgs): EChartsCoreOption => {
    const format = args.tooltipFormatter
        ?? ((point: PiePoint) => defaultPieTooltip(point, args.valueLabel ?? 'Total Time'));

    return {
        animation: false,
        tooltip: {
            ...TOOLTIP_BASE,
            trigger: 'item',
            formatter: (params: unknown) =>
                `<div class="chart-tooltip-new">${format(toPiePoint(params as PieParamsLike))}</div>`,
        },
        legend: { show: false },
        series: [{
            type: 'pie',
            radius: ['38%', '78%'],
            center: ['50%', '50%'],
            selectedMode: 'single',
            cursor: 'pointer',
            avoidLabelOverlap: true,
            labelLine: { length: 26, length2: 30, smooth: 0.2, minTurnAngle: 60 },
            labelLayout: { hideOverlap: true },
            label: {
                show: true,
                fontSize: 11,
                minMargin: 8,
                formatter: (params: unknown) => {
                    const point = toPiePoint(params as PieParamsLike);
                    return `{sliceName|${point.name}}: ${point.formatted}`;
                },
                rich: { sliceName: { fontWeight: 'bold', fontSize: 11 } },
            },
            emphasis: {
                focus: 'self',
                scale: true,
                scaleSize: 5,
            },
            blur: {
                itemStyle: { opacity: INACTIVE_OPACITY },
                label: { opacity: INACTIVE_OPACITY },
                labelLine: { opacity: INACTIVE_OPACITY },
            },
            data: args.slices.map(slice => ({
                name: slice.name,
                value: slice.value,
                formatted: slice.formatted,
                url: slice.url,
                queryParameters: slice.queryParameters,
                ...(slice.color ? { itemStyle: { color: slice.color } } : {}),
            })),
        }],
    };
};
